import { memo, useState } from 'react';
import { Col, Dropdown, Popconfirm, Row, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { dateToString, getFullName } from '@/utils';
import { useDeleteParentNoteMutation, useGetParentNotesQuery } from '@/services/parent-api';
import { EditSvg, EllipsisVSvg, PlusSvg, TrashSvg } from '@/assets/icons';
import Panel from '@/components/panel';
import Table from '@/components/table';
import Button from '@/components/button';
import EditModal from './edit-modal';
import moment from 'moment';

const FamilyNotes = ({ parentId, permissions }) => {
  const { data, isLoading, refetch } = useGetParentNotesQuery({ parentId });
  const [deleteNote] = useDeleteParentNoteMutation();
  const [editInfo, setEditInfo] = useState();

  const columns = [
    {
      title: 'Date/Person',
      width: 110,
      render: (value) => (
        <>
          {dateToString(value.createDate)}
          <br />
          {getFullName(value)}
        </>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'noteType',
      render: (value) => value.name,
    },
    permissions.adminStaffAndSuper && {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        // <Space size="small">
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditSvg />,
                label: 'Edit',
                onClick: () => setEditInfo(record),
              },
              {
                key: 'delete',
                icon: <TrashSvg />,
                label: (
                  <Popconfirm
                    title="Are you sure to delete this?"
                    icon={<QuestionCircleOutlined style={{ color: '#ff4d4f' }} />}
                    okText="Yes"
                    cancelText="No"
                    onConfirm={async () => {
                      try {
                        await deleteNote({ parentId, id: record.id }).unwrap();
                        refetch();
                      } catch (err) {}
                    }}
                  >
                    Delete
                  </Popconfirm>
                ),
              },
            ].filter((item) => item),
          }}
        >
          <Button type="link" icon={<EllipsisVSvg fontSize="16" />} />
        </Dropdown>
        // </Space>
      ),
    },
  ].filter((item) => item);

  const expandedRowRender = (record) => {
    return (
      <>
        {record.childId > 0 && (
          <Row style={{ margin: '0 0 0 50px' }}>
            <Col md={4}>
              <strong>Child : </strong>
            </Col>
            <Col md={20}>{record.childFirstName} {record.childLastName} ( {record.childCode} )</Col>
          </Row>
        )}
        {record.dateLabel && (
          <Row style={{ margin: '0 0 0 50px' }}>
            <Col md={4}>
              <strong>{record.dateLabel} : </strong>
            </Col>
            <Col md={20}>{moment(record.noteDate).format('MM/DD/YYYY')}</Col>
          </Row>
        )}
        {record.reason && (
          <Row style={{ margin: '0 0 0 50px' }}>
            <Col md={4}>
              <strong>Reason : </strong>
            </Col>
            <Col md={20}>{record.reason}</Col>
          </Row>
        )}
        {record.present && (
          <Row style={{ margin: '0 0 0 50px' }}>
            <Col md={4}>
              <strong>Present : </strong>
            </Col>
            <Col md={20}>{record.present}</Col>
          </Row>
        )}
        <Row style={{ margin: '0 0 0 50px' }}>
          <Col md={4}>
            <strong>Note : </strong>
          </Col>
          <Col md={20}>{record.note}</Col>
        </Row>
      </>
    );
  };

  return (
    <Panel
      // icon={<StickyNoteSvg />}
      title="Add Family Notes"
      extra={
        (permissions.adminStaffAndSuper || permissions.writeParent) && (
          <Tooltip title="Add Note">
            <Button type="link" icon={<PlusSvg fontSize="16" />} onClick={() => setEditInfo({})} />
          </Tooltip>
        )
      }
      loading={isLoading}
    >
      {data &&
        (data.length ? (
          <Table columns={columns} dataSource={data} rowKey="id" size="small" expandedRowRender={expandedRowRender} />
        ) : (
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
            No Notes
          </Typography.Text>
        ))}

      <EditModal parentId={parentId} item={editInfo} refetch={refetch} onClose={() => setEditInfo()} />
    </Panel>
  );
};

export default memo(FamilyNotes);
